import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const translations = {
  es: {
    // Header
    nav: {
      services: 'Servicios',
      results: 'Resultados',
      process: 'Proceso',
      contact: 'Contacto',
      schedule: 'Agendar Cita',
      call: 'Llamar'
    },
    // Hero
    hero: {
      tagline: 'Confianza que se ve',
      title: 'Trasplante Capilar FUE en Tijuana:',
      subtitle: 'Resultados naturales y personalizados.',
      description: 'Solo atendemos a un paciente al día, lo que nos permite ofrecer una atención personalizada y un seguimiento antes, durante y después del procedimiento.',
      badge1: 'Resultados permanentes',
      badge2: '1 paciente por día',
      badge3: '+10 años experiencia',
      stat1: '500+',
      stat1Label: 'Pacientes satisfechos',
      stat2: '10+',
      stat2Label: 'Años de experiencia',
      stat3: '100%',
      stat3Label: 'Éxito en procedimientos',
      formTitle: 'Agenda tu Consulta Gratis',
      formSubtitle: 'Completa el formulario y te contactaremos',
      formName: 'Nombre Completo',
      formEmail: 'Correo Electrónico',
      formPhone: 'Teléfono',
      formService: 'Servicio de Interés',
      formServicePlaceholder: 'Selecciona un servicio',
      formPhotos: 'Fotografías del Paciente',
      formPhotosDesc: 'Sube fotos desde estos ángulos para una mejor evaluación: Frontal | 45° | Lateral | Posterior',
      formMessage: 'Mensaje (Opcional)',
      formMessagePlaceholder: 'Cuéntanos más sobre tu caso...',
      formSubmit: 'Solicitar Cita Gratis',
      formSubmitting: 'Enviando...',
      formDisclaimer: 'Al enviar este formulario, aceptas ser contactado por nuestro equipo.',
      photoFront: 'Frontal',
      photoAngle: '45°',
      photoSide: 'Lateral',
      photoBack: 'Posterior'
    },
    // Services
    services: {
      tagline: 'Nuestros Servicios',
      title: 'Soluciones Personalizadas para Tu',
      titleHighlight: 'Restauración Capilar',
      description: 'Cada paciente es único. Ofrecemos tratamientos individualizados con la más alta tecnología y médicos certificados.',
      service1: 'Trasplante Capilar con Técnica FUE',
      service1Desc: 'Recupera tu cabello con resultados naturales y duraderos gracias al trasplante de tus propios folículos.',
      service2: 'Injerto de Barba y Bigote',
      service2Desc: 'Logra la barba que siempre quisiste. Densidad y forma natural.',
      service3: 'Injerto Capilar para Mujeres',
      service3Desc: 'Recupera tu volumen y feminidad. Tratamiento discreto y natural.',
      service4: 'Injerto de Cejas',
      service4Desc: 'Define tu mirada con cejas naturales y perfectamente diseñadas para tu rostro.'
    },
    serviceOptions: {
      'trasplante-capilar': 'Trasplante Capilar',
      'injerto-barba': 'Injerto de Barba y Bigote',
      'injerto-mujeres': 'Injerto Capilar para Mujeres',
      'injerto-cejas': 'Injerto de Cejas'
    },
    // FUE Technique
    fue: {
      tagline: 'Tecnología Avanzada',
      title: 'La técnica de trasplante capilar',
      titleHighlight: 'FUE en Bianhi',
      description: 'La técnica FUE (extracción de unidades foliculares) es un método moderno y mínimamente invasivo para el trasplante capilar. Cada unidad folicular se extrae individualmente utilizando un punzón de 0,6-1 mm, lo que permite una recuperación más rápida.',
      benefit2: 'Recuperación rápida',
      benefit3: 'Resultados naturales y permanentes',
      benefit4: 'Procedimiento ambulatorio con anestesia local'
    },
    // Before/After Gallery
    gallery: {
      tagline: 'Casos Reales',
      title: 'Resultados que',
      titleHighlight: 'Hablan por Sí Solos',
      description: 'Conoce las transformaciones de nuestros pacientes. Resultados naturales respaldados por años de experiencia.',
      before: 'ANTES',
      after: 'INJERTO',
      hoverHint: 'Pasa el cursor para ver resultado',
      years: 'años',
      grafts: 'injertos',
      cta: 'Quiero Resultados Así'
    },
    // Trust Proof
    trustProof: {
      title: 'La confianza de los pacientes que buscan',
      titleHighlight: 'resultados naturales',
      titleEnd: 'para la restauración capilar',
      description: 'Los pacientes reales eligen un enfoque personalizado y con orientación médica para restaurar su cabello con confianza.',
      point1: 'Procedimientos personalizados para cada paciente',
      point2: 'Equipo médico certificado y experimentado',
      point3: 'Resultados permanentes en cada procedimiento'
    },
    // Google Reviews
    reviews: {
      tagline: 'Opiniones de Google',
      title: 'Lo que Dicen Nuestros',
      titleHighlight: 'Pacientes',
      opinions: 'opiniones',
      onGoogle: 'en Google',
      verified: 'Verificado en Google',
      viewAll: 'Ver todas las opiniones en Google'
    },
    // Doctors
    doctors: {
      tagline: 'Nuestro Equipo',
      title: 'Conoce a Nuestras',
      titleHighlight: 'Especialistas',
      description: 'Médicos certificados con años de experiencia y capacitación internacional en las técnicas más avanzadas de regeneración capilar.',
      education: 'Educación',
      license: 'Cédula',
      experience: 'Experiencia',
      teamNote: 'Nuestro equipo trabaja bajo la dirección del',
      teamNoteEnd: 'garantizando los más altos estándares de calidad y profesionalismo en cada procedimiento.'
    },
    // Trust Section
    trust: {
      tagline: '¿Por qué elegirnos?',
      title: '¿Por qué elegir BIANHI para su',
      titleHighlight: 'trasplante capilar?',
      description: 'En Bianhi, nos comprometemos con resultados excepcionales y una experiencia de primera clase.',
      item1: 'Atención médica personalizada',
      item1Desc: 'Nos centramos en un solo paciente al día para garantizar la atención, la privacidad y la calidad.',
      item2: 'Técnica FUE avanzada',
      item2Desc: 'Un método mínimamente invasivo diseñado para obtener resultados naturales.',
      item3: 'Evaluación médica completa',
      item3Desc: 'Revisamos los parámetros de salud importantes antes de cualquier procedimiento.',
      item4: 'Seguimiento durante un año',
      item4Desc: 'Asesoramiento continuo durante todo el proceso de recuperación.',
      guaranteeTitle: '¡Resultados permanentes!',
      guaranteeDesc: 'Los folículos pilosos de la zona donante son naturalmente resistentes a la caída del cabello y se trasplantan con cuidado para lograr resultados duraderos.'
    },
    // Process
    process: {
      tagline: 'Nuestro Proceso',
      title: 'Déjalo en Manos de',
      titleHighlight: 'Expertos',
      description: 'Pasos a seguir para realizar tu trasplante capilar',
      hoverHint: 'Pasa el cursor',
      step1: 'Consulta inicial',
      step1Sub: 'El primer paso para una nueva vida.',
      step1Desc: 'Revisamos las fotos y evaluamos el estado de su cabello.',
      step2: 'Preparación personalizada',
      step2Sub: 'Instrucciones claras y precisas.',
      step2Desc: 'Recibirá instrucciones claras antes del procedimiento.',
      step3: 'Procedimiento FUE',
      step3Sub: 'Técnica mínimamente invasiva.',
      step3Desc: 'Los folículos pilosos se extraen e implantan cuidadosamente.',
      step4: 'Seguimiento posterior',
      step4Sub: 'Acompañamiento continuo.',
      step4Desc: 'Apoyo continuo durante la recuperación.',
      step5: 'Resultados visibles',
      step5Sub: 'Tu nueva imagen.',
      step5Desc: 'Crecimiento natural del cabello con el paso del tiempo.',
      step6: 'Resultados',
      step6Sub: 'Miles de pacientes con resultados exitosos.',
      step6Desc: 'Recuerda que con el trasplante capilar no se obtiene un resultado inmediato, después del injerto en el 95% de los pacientes el pelo injertado se cae, quedando en el interior las unidades foliculares que formarán nuevos cabellos.'
    },
    // Contact
    contact: {
      tagline: 'Contáctanos',
      title: 'Estamos Listos para',
      titleHighlight: 'Ayudarte',
      description: '¿Tienes preguntas? Nuestro equipo está listo para atenderte.',
      callUs: 'Llámanos',
      phoneLine: 'Línea de información y citas',
      whatsapp: 'WhatsApp',
      whatsappDesc: 'Escríbenos. Queremos saber de ti.',
      location: 'Ubicación',
      hours: 'Horarios',
      monFri: 'Lun - Vie',
      sat: 'Sábados',
      sun: 'Domingos',
      closed: 'Cerrado'
    },
    // CTA
    cta: {
      title: '¿Listo para Recuperar tu Cabello?',
      description: 'Agenda tu consulta gratuita hoy y da el primer paso hacia una nueva imagen.',
      callNow: 'Llamar Ahora'
    },
    // Footer
    footer: {
      description: 'Especialistas en regeneración capilar en Tijuana, MX. Más de 10 años transformando vidas con resultados naturales.',
      services: 'Servicios',
      contact: 'Contacto',
      rights: 'Todos los derechos reservados.'
    },
    // Thank You Page
    thankYou: {
      title: '¡Gracias por',
      titleHighlight: 'Contactarnos',
      message: 'Hemos recibido tu mensaje correctamente.',
      description: 'Uno de nuestros asesores se pondrá en contacto contigo en breve para brindarte atención personalizada.',
      immediate: 'Si deseas atención inmediata, contáctanos ahora:',
      privacy: 'Tu información está protegida y será tratada con total confidencialidad.',
      backHome: 'Volver al Inicio'
    }
  },
  en: {
    // Header
    nav: {
      services: 'Services',
      results: 'Results',
      process: 'Process',
      contact: 'Contact',
      schedule: 'Book Appointment',
      call: 'Call'
    },
    // Hero
    hero: {
      tagline: 'Confidence you can see',
      title: 'FUE Hair Transplant in Tijuana:',
      subtitle: 'Natural and personalized results.',
      description: 'We only treat one patient per day, allowing us to offer personalized care and follow-up before, during, and after the procedure.',
      badge1: 'Long-Lasting Results',
      badge2: '1 patient per day',
      badge3: '+10 years experience',
      stat1: '500+',
      stat1Label: 'Satisfied patients',
      stat2: '10+',
      stat2Label: 'Years of experience',
      stat3: '100%',
      stat3Label: 'Procedure success',
      formTitle: 'Schedule Your Free Consultation',
      formSubtitle: 'Fill out the form and we will contact you',
      formName: 'Full Name',
      formEmail: 'Email',
      formPhone: 'Phone',
      formService: 'Service of Interest',
      formServicePlaceholder: 'Select a service',
      formPhotos: 'Patient Photos',
      formPhotosDesc: 'Upload photos from these angles for a better evaluation: Front | 45° | Side | Back',
      formMessage: 'Message (Optional)',
      formMessagePlaceholder: 'Tell us more about your case...',
      formSubmit: 'Request Free Appointment',
      formSubmitting: 'Sending...',
      formDisclaimer: 'By submitting this form, you agree to be contacted by our team.',
      photoFront: 'Front',
      photoAngle: '45°',
      photoSide: 'Side',
      photoBack: 'Back'
    },
    // Services
    services: {
      tagline: 'Our Services',
      title: 'Personalized Solutions for Your',
      titleHighlight: 'Hair Restoration',
      description: 'Every patient is unique. We offer individualized treatments with the highest technology and certified doctors.',
      service1: 'Hair Transplant Using the FUE Technique',
      service1Desc: 'Restore your hair with natural, long-lasting results thanks to a transplant using your own hair follicles.',
      service2: 'Beard & Mustache Graft',
      service2Desc: 'Get the beard you always wanted. Natural density and shape.',
      service3: 'Hair Graft for Women',
      service3Desc: 'Restore your volume and femininity. Discreet and natural treatment.',
      service4: 'Eyebrow Graft',
      service4Desc: 'Define your look with natural eyebrows perfectly designed for your face.'
    },
    serviceOptions: {
      'trasplante-capilar': 'Hair Transplant',
      'injerto-barba': 'Beard & Mustache Graft',
      'injerto-mujeres': 'Hair Graft for Women',
      'injerto-cejas': 'Eyebrow Graft'
    },
    // FUE Technique
    fue: {
      tagline: 'Advanced Technology',
      title: 'FUE hair transplant technique',
      titleHighlight: 'at Bianhi',
      description: 'The FUE (Follicular Unit Extraction) technique is a modern, minimally invasive method for hair transplantation. Each follicular unit is extracted individually using a 0.6-1mm punch, allowing for faster recovery and virtually invisible scars.',
      benefit1: 'No visible scars or sutures',
      benefit2: 'Quick recovery',
      benefit3: 'Natural and permanent results',
      benefit4: 'Outpatient procedure with local anesthesia'
    },
    // Before/After Gallery
    gallery: {
      tagline: 'Real Cases',
      title: 'Results that',
      titleHighlight: 'Speak for Themselves',
      description: 'Discover our patients\' transformations. Natural results backed by years of experience.',
      before: 'BEFORE',
      after: 'GRAFT',
      hoverHint: 'Hover to see result',
      years: 'years old',
      grafts: 'grafts',
      cta: 'I Want Results Like This'
    },
    // Trust Proof
    trustProof: {
      title: 'The confidence of patients seeking',
      titleHighlight: 'natural results',
      titleEnd: 'for hair restoration',
      description: 'Real patients choose a personalized, medically-guided approach to restore their hair with confidence.',
      point1: 'Personalized procedures for each patient',
      point2: 'Certified and experienced medical team',
      point3: 'Consistent results with every procedure'
    },
    // Google Reviews
    reviews: {
      tagline: 'Google Reviews',
      title: 'What Our',
      titleHighlight: 'Patients Say',
      opinions: 'reviews',
      onGoogle: 'on Google',
      verified: 'Verified on Google',
      viewAll: 'View all reviews on Google'
    },
    // Doctors
    doctors: {
      tagline: 'Our Team',
      title: 'Meet Our',
      titleHighlight: 'Specialists',
      description: 'Certified doctors with years of experience and international training in the most advanced hair restoration techniques.',
      education: 'Education',
      license: 'License',
      experience: 'Experience',
      teamNote: 'Our team works under the direction of',
      teamNoteEnd: 'ensuring the highest standards of quality and professionalism in every procedure.'
    },
    // Trust Section
    trust: {
      tagline: 'Why Choose Us?',
      title: 'Why Choose BIANHI for Your',
      titleHighlight: 'Hair Transplant?',
      description: 'At Bianhi, we are committed to exceptional results and a first-class experience.',
      item1: 'Personalized medical care',
      item1Desc: 'We focus on one patient per day to ensure attention, privacy, and quality.',
      item2: 'Advanced FUE technique',
      item2Desc: 'A minimally invasive method designed for natural results.',
      item3: 'Complete medical evaluation',
      item3Desc: 'We review important health parameters before any procedure.',
      item4: 'One-year follow-up',
      item4Desc: 'Continuous guidance throughout your recovery process.',
      guaranteeTitle: 'Long-Lasting Results',
      guaranteeDesc: 'Hair follicles from your donor area are naturally resistant to hair loss and are carefully transplanted to achieve results designed to last.'
    },
    // Process
    process: {
      tagline: 'Our Process',
      title: 'Leave it to the',
      titleHighlight: 'Experts',
      description: 'Steps to perform your hair transplant',
      hoverHint: 'Hover to see',
      step1: 'Initial consultation',
      step1Sub: 'The first step to a new life.',
      step1Desc: 'We review photos and evaluate your hair condition.',
      step2: 'Personalized preparation',
      step2Sub: 'Clear and precise instructions.',
      step2Desc: 'You will receive clear instructions before the procedure.',
      step3: 'FUE procedure',
      step3Sub: 'Minimally invasive technique.',
      step3Desc: 'Hair follicles are carefully extracted and implanted.',
      step4: 'Post-procedure follow-up',
      step4Sub: 'Continuous support.',
      step4Desc: 'Ongoing support during recovery.',
      step5: 'Visible results',
      step5Sub: 'Your new image.',
      step5Desc: 'Natural hair growth over time.',
      step6: 'Results',
      step6Sub: 'Thousands of patients with successful results.',
      step6Desc: 'Remember that with hair transplant you don\'t get immediate results. After grafting, in 95% of patients the grafted hair falls out, leaving the follicular units inside that will form new hair.'
    },
    // Contact
    contact: {
      tagline: 'Contact Us',
      title: 'We\'re Ready to',
      titleHighlight: 'Help You',
      description: 'Have questions? Our team is ready to assist you.',
      callUs: 'Call Us',
      phoneLine: 'Information and appointments line',
      whatsapp: 'WhatsApp',
      whatsappDesc: 'Write to us. We want to hear from you.',
      location: 'Location',
      hours: 'Hours',
      monFri: 'Mon - Fri',
      sat: 'Saturdays',
      sun: 'Sundays',
      closed: 'Closed'
    },
    // CTA
    cta: {
      title: 'Ready to Restore Your Hair?',
      description: 'Schedule your free consultation today and take the first step towards a new image.',
      callNow: 'Call Now'
    },
    // Footer
    footer: {
      description: 'Hair restoration specialists in Tijuana, MX. Over 10 years transforming lives with natural results.',
      services: 'Services',
      contact: 'Contact',
      rights: 'All rights reserved.'
    },
    // Thank You Page
    thankYou: {
      title: 'Thank You for',
      titleHighlight: 'Contacting Us',
      message: 'We have received your message successfully.',
      description: 'One of our advisors will contact you shortly to provide personalized assistance.',
      immediate: 'For immediate assistance, contact us now:',
      privacy: 'Your information is protected and will be treated with complete confidentiality.',
      backHome: 'Back to Home'
    }
  }
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [language, setLanguage] = useState('es');

  useEffect(() => {
    const isEnglish = location.pathname.startsWith('/en');
    setLanguage(isEnglish ? 'en' : 'es');
  }, [location.pathname]);

  const switchLanguage = useCallback((lang) => {
    const currentPath = location.pathname;
    
    if (lang === 'en') {
      if (!currentPath.startsWith('/en')) {
        const newPath = currentPath === '/' ? '/en' : `/en${currentPath}`;
        navigate(newPath);
      }
    } else {
      if (currentPath.startsWith('/en')) {
        const newPath = currentPath.replace('/en', '') || '/';
        navigate(newPath);
      }
    }
    setLanguage(lang);
  }, [location.pathname, navigate]);

  const t = useCallback((key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  }, [language]);

  const contextValue = useMemo(() => ({
    language, switchLanguage, t, translations: translations[language]
  }), [language, switchLanguage, t]);

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
